/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import {
  MdOutlineDescription,
  MdOutlineLocationOn,
  MdHelpOutline,
} from 'react-icons/md';
import { BiTimeFive, BiWallet } from 'react-icons/bi';
import { FiZap, FiImage } from 'react-icons/fi';
import ModernCard from './ModernCard';
import { Job } from '../../../../utils/type';
import { formatCurrency } from '../../../../utils/currency';

const TIMEFRAME_LABEL: Record<string, string> = {
  asap: 'As soon as possible',
  '2weeks': 'Within 2 weeks',
  month: 'Within a month',
  flexible: 'Flexible',
};

/**
 * RequestSummaryCard — supports two orientations:
 *  - 'vertical' (default): narrow sidebar column, full detail
 *  - 'horizontal': a single condensed row that spans the full page width
 */
const RequestSummaryCard = ({
  job,
  jobs,
  t,
  orientation = 'vertical',
}: {
  job: Job;
  jobs?: Job[];
  t: any;
  orientation?: 'vertical' | 'horizontal';
}) => {
  const [isScopeExpanded, setIsScopeExpanded] = useState(false);
  const [showAllDetails, setShowAllDetails] = useState(false);
  const [expandedPrompts, setExpandedPrompts] = useState<Set<string>>(
    new Set()
  );

  // Check if this request is a broadcast request batch
  const isBroadcast = Boolean(
    (job as any).batchId || (jobs && jobs.length > 1)
  );

  const additionalServices: Array<{
    id: string;
    serviceItem: string;
    price: number;
    images?: string;
  }> = (job as any).additionalServices ?? [];

  const promptAnswers = job.promptAnswers ?? [];
  const hasBudget =
    typeof job.budgetMin === 'number' || typeof job.budgetMax === 'number';

  // Helper function to render boolean answers as Yes/No
  const renderAnswerValue = (answerItem: any): string => {
    if (answerItem.type === 'BOOLEAN') {
      return answerItem.answer === true ? 'Yes' : 'No';
    }
    if (Array.isArray(answerItem.answer)) {
      return answerItem.answer.join(', ');
    }
    return String(answerItem.answer);
  };

  // Helper function to toggle prompt expansion
  const togglePromptExpansion = (promptId: string) => {
    const newSet = new Set(expandedPrompts);
    if (newSet.has(promptId)) {
      newSet.delete(promptId);
    } else {
      newSet.add(promptId);
    }
    setExpandedPrompts(newSet);
  };

  // Check if prompt answer needs expansion (LONG_TEXT type)
  const isLongText = (answerItem: any): boolean =>
    answerItem.type === 'LONG_TEXT';
  const isPromptExpanded = (promptId: string): boolean =>
    expandedPrompts.has(promptId);

  if (orientation === 'horizontal') {
    return (
      <ModernCard
        title={
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold">
              <MdOutlineDescription size={18} />
            </div>
            <span className="font-semibold text-sm text-slate-800">
              {t('jobs.request_summary', 'Request Summary')}
            </span>
          </div>
        }
      >
        <div className="flex flex-col space-y-2">
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-4">
            {/* Description */}
            {job.scopeDescription && (
              <div className="flex-1 text-xs min-w-0 rounded-xl bg-slate-50 p-3.5 border border-slate-200/70">
                <div className="flex items-center gap-1.5 mb-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <MdOutlineDescription size={14} className="text-primary" />
                  Description
                </div>
                <p
                  className={`text-xs text-slate-700 leading-relaxed ${
                    !isScopeExpanded ? 'line-clamp-2' : 'whitespace-pre-line'
                  }`}
                >
                  {job.scopeDescription}
                </p>
                {job.scopeDescription.length > 140 && (
                  <button
                    type="button"
                    onClick={() => setIsScopeExpanded(!isScopeExpanded)}
                    className="mt-1.5 text-xs font-semibold text-primary hover:underline"
                  >
                    {isScopeExpanded ? 'Show less' : 'Read full scope'}
                  </button>
                )}
              </div>
            )}

            {/* Quick-fact chips */}
            <div className="flex flex-wrap lg:flex-nowrap items-stretch gap-3 shrink-0">
              <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200/60 min-w-[160px]">
                <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600 shrink-0">
                  <BiTimeFive size={16} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    {t('jobs.timeframe', 'Timeframe')}
                  </p>
                  <p className="text-xs font-semibold text-slate-800 truncate">
                    {TIMEFRAME_LABEL[job.timeframePreference] ||
                      job.timeframePreference}
                  </p>
                </div>
              </div>

              {job.location && (
                <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200/60 min-w-[160px]">
                  <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 shrink-0">
                    <MdOutlineLocationOn size={16} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {t('jobs.location', 'Location')}
                    </p>
                    <p className="text-xs text-slate-800 truncate">
                      {job.location}
                    </p>
                  </div>
                </div>
              )}

              {hasBudget && (
                <div className="flex items-start gap-2.5 px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200/60 min-w-[180px]">
                  <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-600 shrink-0 mt-0.5">
                    <BiWallet size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {t('jobs.budget', 'Est. Budget')}
                    </p>
                    <div className="text-xs font-semibold text-slate-800 leading-tight mt-0.5 space-y-0.5">
                      {typeof job.budgetMin === 'number' && (
                        <div>Min: {formatCurrency(job.budgetMin)}</div>
                      )}
                      {typeof job.budgetMax === 'number' && (
                        <div>Max: {formatCurrency(job.budgetMax)}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!isBroadcast && additionalServices.length > 0 && (
                <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200/60 min-w-[160px]">
                  <div className="p-1.5 rounded-lg bg-primary/10 text-primary shrink-0">
                    <FiZap size={16} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {t('jobs.additional_services', 'Add-ons')}
                    </p>
                    <p className="text-xs text-slate-800 truncate">
                      {additionalServices.length} requested
                    </p>
                  </div>
                </div>
              )}

              {job.photos && job.photos.length > 0 && (
                <div className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200/60 min-w-[140px]">
                  <div className="p-1.5 rounded-lg bg-slate-500/10 text-slate-600 shrink-0">
                    <FiImage size={16} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {t('jobs.attachments', 'Photos')}
                    </p>
                    <p className="text-xs text-slate-800 truncate">
                      {job.photos.length} attached
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModernCard>
    );
  }

  // Vertical Orientation
  return (
    <ModernCard
      title={
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold">
            <MdOutlineDescription size={18} />
          </div>
          <span className="font-semibold text-sm text-slate-800">
            {t('jobs.request_summary', 'Request Summary')}
          </span>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Description with Read More fallback */}
        {job.scopeDescription && (
          <div className="rounded-xl bg-slate-50 p-3 border border-slate-200/70 relative">
            <div className="flex items-center gap-1.5 mb-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <MdOutlineDescription size={15} className="text-primary" />
              Description
            </div>
            <p
              className={`text-xs text-slate-700 whitespace-pre-line leading-relaxed ${
                !isScopeExpanded && job.scopeDescription.length > 150
                  ? 'line-clamp-3'
                  : ''
              }`}
            >
              {job.scopeDescription}
            </p>
            {job.scopeDescription.length > 150 && (
              <button
                type="button"
                onClick={() => setIsScopeExpanded(!isScopeExpanded)}
                className="mt-2 text-xs font-semibold text-primary hover:underline flex items-center gap-0.5"
              >
                {isScopeExpanded ? 'Show less' : 'Read full scope'}
              </button>
            )}
          </div>
        )}

        {/* Timeframe, Location, and Budget Cards */}
        <div className="space-y-2">
          <div className="flex items-start gap-3 p-2.5 rounded-xl bg-slate-50 border border-slate-200/60">
            <div className="p-1 rounded-lg bg-amber-500/10 text-amber-600 shrink-0 mt-0.5">
              <BiTimeFive size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                {t('jobs.timeframe', 'Timeframe')}
              </p>
              <p className="text-xs font-semibold text-slate-800 truncate mt-0.5">
                {TIMEFRAME_LABEL[job.timeframePreference] ||
                  job.timeframePreference}
              </p>
            </div>
          </div>

          {job.location && (
            <div className="flex items-start gap-3 p-2.5 rounded-xl bg-slate-50 border border-slate-200/60">
              <div className="p-1 rounded-lg bg-emerald-500/10 text-emerald-600 shrink-0 mt-0.5">
                <MdOutlineLocationOn size={18} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  {t('jobs.location', 'Location')}
                </p>
                <p className="text-xs font-semibold text-slate-800 truncate mt-0.5">
                  {job.location}
                </p>
              </div>
            </div>
          )}

          {hasBudget && (
            <div className="flex items-start gap-3 p-2.5 rounded-xl bg-slate-50 border border-slate-200/60">
              <div className="p-1 rounded-lg bg-purple-500/10 text-purple-600 shrink-0 mt-0.5">
                <BiWallet size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  {t('jobs.budget', 'Estimated Budget')}
                </p>
                <div className="text-xs font-bold text-slate-800 mt-1 space-y-0.5">
                  {typeof job.budgetMin === 'number' && (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 font-xs">Min:</span>
                      <span>{formatCurrency(job.budgetMin)}</span>
                    </div>
                  )}
                  {typeof job.budgetMax === 'number' && (
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 font-xs">Max:</span>
                      <span>{formatCurrency(job.budgetMax)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Requested Add-on Services Badges (Hidden for Broadcast) */}
        {!isBroadcast && additionalServices.length > 0 && (
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2.5">
              {t('jobs.additional_services', 'Add-on Services Requested')}
            </p>
            <div className="space-y-2">
              {additionalServices.map((svc) => (
                <div
                  key={svc.id}
                  className="flex items-center gap-3 p-2 rounded-xl bg-slate-50 border border-slate-200/50 hover:bg-slate-100/60 transition-colors"
                >
                  {svc.images ? (
                    <img
                      src={svc.images}
                      alt={svc.serviceItem}
                      className="w-10 h-10 object-cover rounded-lg border border-slate-200 shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <FiZap size={18} />
                    </div>
                  )}
                  <p className="text-xs font-medium text-slate-700 flex-1 truncate">
                    {svc.serviceItem}
                  </p>
                  <span className="text-xs font-semibold text-secondary bg-white px-2.5 py-1 rounded-lg border border-slate-200/60 shadow-2xs">
                    {formatCurrency(svc.price)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photos Grid */}
        {job.photos && job.photos.length > 0 && (
          <div className="pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2.5">
              {t('jobs.attachments', 'Attached Photos')} ({job.photos.length})
            </p>
            <div className="flex flex-wrap gap-2.5">
              {job.photos.map((url, i) => (
                <a
                  key={`${url}-${i}`}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="group relative overflow-hidden rounded-xl border border-slate-200 hover:border-primary transition-all shadow-2xs hover:shadow-xs"
                >
                  <img
                    src={url}
                    alt={`attachment ${i + 1}`}
                    className="w-16 h-16 object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Additional Details Section - Moved to Bottom with Collapse */}
        {!isBroadcast &&
          job.initiatedBy !== 'provider' &&
          promptAnswers.length > 0 && (
            <div className="pt-3 border-t border-slate-100">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <MdHelpOutline size={14} className="text-primary" />
                  Additional Details
                </p>
                <button
                  type="button"
                  onClick={() => setShowAllDetails(!showAllDetails)}
                  className="text-xs font-semibold text-primary hover:underline transition-colors"
                >
                  {showAllDetails ? 'Hide' : 'View All'}
                </button>
              </div>

              {showAllDetails ? (
                <div className="space-y-2.5">
                  {promptAnswers.map((answerItem, idx) => (
                    <div
                      key={answerItem.promptId || idx}
                      className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/60 text-xs"
                    >
                      <p className="font-bold text-slate-500 mb-0.5">
                        {answerItem.label ||
                          (answerItem as any).promptText ||
                          'Question'}
                      </p>

                      {isLongText(answerItem) ? (
                        <div>
                          <p
                            className={`font-semibold text-slate-800 whitespace-pre-line leading-relaxed ${
                              !isPromptExpanded(
                                answerItem.promptId || `prompt-${idx}`
                              )
                                ? 'line-clamp-2'
                                : ''
                            }`}
                          >
                            {renderAnswerValue(answerItem)}
                          </p>
                          {renderAnswerValue(answerItem).split('\n').length >
                            2 && (
                            <button
                              type="button"
                              onClick={() =>
                                togglePromptExpansion(
                                  answerItem.promptId || `prompt-${idx}`
                                )
                              }
                              className="mt-1.5 text-xs font-semibold text-primary hover:underline"
                            >
                              {isPromptExpanded(
                                answerItem.promptId || `prompt-${idx}`
                              )
                                ? 'Show less'
                                : 'View more'}
                            </button>
                          )}
                        </div>
                      ) : (
                        <p className="font-semibold text-slate-800">
                          {renderAnswerValue(answerItem)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}
      </div>
    </ModernCard>
  );
};

export default RequestSummaryCard;
